import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { formatDate } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import './Sections.css';
import './Reports.css';

// GST Reports (gst_report_layout.xml / gst_invoice_sale.xml / gst_product_sale_layout.xml).
// Line-level GST sale register + HSN / rate summaries and a B2B / B2C split.
// Taxable amount per line = line total; tax = taxable × rate%; CGST = SGST =
// tax/2; IGST = 0 (intra-state assumption, matching the app's default).

type ReportKey = 'gst_sale' | 'hsn_summary' | 'rate_summary' | 'b2b' | 'b2c';

const CARDS: { key: ReportKey; title: string; desc: string }[] = [
  { key: 'gst_sale', title: 'GST Sale Register', desc: 'Line-wise: taxable, CGST, SGST, IGST, total' },
  { key: 'hsn_summary', title: 'HSN Summary', desc: 'Taxable & tax grouped by HSN code' },
  { key: 'rate_summary', title: 'GST Rate Summary', desc: 'Taxable & tax grouped by GST rate' },
  { key: 'b2b', title: 'B2B Sales', desc: 'Bills to GSTIN-registered customers' },
  { key: 'b2c', title: 'B2C Sales', desc: 'Bills to walk-in / unregistered customers' },
];

interface Result {
  title: string;
  columns: string[];
  rows: (string | number)[][];
  numericCols?: number[];
}

const GstReports: React.FC = () => {
  const { sales, customers, products, formatMoney } = usePos();
  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 8) + '01';
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [active, setActive] = useState<ReportKey | null>(null);

  const inRange = (iso: string) => {
    const t = new Date(iso).getTime();
    return t >= new Date(start).getTime() && t <= new Date(end + 'T23:59:59').getTime();
  };

  const completed = useMemo(
    () => sales.filter((s) => s.status === 'completed' && s.total > 0 && inRange(s.timestamp)),
    [sales, start, end] // eslint-disable-line
  );

  const hsnOf = (productId: string) => products.find((p) => p.id === productId)?.hsnCode || '—';
  const gstinOf = (customerId?: string) => customers.find((c) => c.id === customerId)?.gstin;

  const result: Result | null = useMemo(() => {
    if (!active) return null;
    const m = (n: number) => formatMoney(n);

    switch (active) {
      case 'gst_sale': {
        const rows: (string | number)[][] = [];
        let sr = 0;
        completed.forEach((s) =>
          s.items.forEach((i) => {
            sr += 1;
            const taxable = i.total;
            const tax = taxable * (i.taxRatePercent / 100);
            rows.push([
              sr,
              formatDate(s.timestamp),
              s.billNo,
              i.name,
              m(i.unitPrice),
              i.quantity,
              m(taxable),
              m(tax / 2),
              m(tax / 2),
              m(0),
              m(taxable + tax),
            ]);
          })
        );
        return {
          title: 'GST Sale Register',
          columns: ['SR', 'Date', 'Bill No', 'Product', 'Price', 'Qty', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'],
          rows,
          numericCols: [4, 5, 6, 7, 8, 9, 10],
        };
      }

      case 'hsn_summary': {
        const map = new Map<string, { qty: number; taxable: number; tax: number }>();
        completed.forEach((s) =>
          s.items.forEach((i) => {
            const k = hsnOf(i.productId);
            const r = map.get(k) ?? { qty: 0, taxable: 0, tax: 0 };
            r.qty += i.quantity;
            r.taxable += i.total;
            r.tax += i.total * (i.taxRatePercent / 100);
            map.set(k, r);
          })
        );
        return {
          title: 'HSN Summary',
          columns: ['HSN', 'Qty', 'Taxable', 'CGST', 'SGST', 'Total'],
          rows: Array.from(map.entries()).map(([k, v]) => [k, v.qty, m(v.taxable), m(v.tax / 2), m(v.tax / 2), m(v.taxable + v.tax)]),
          numericCols: [1, 2, 3, 4, 5],
        };
      }

      case 'rate_summary': {
        const map = new Map<number, { taxable: number; tax: number }>();
        completed.forEach((s) =>
          s.items.forEach((i) => {
            const r = map.get(i.taxRatePercent) ?? { taxable: 0, tax: 0 };
            r.taxable += i.total;
            r.tax += i.total * (i.taxRatePercent / 100);
            map.set(i.taxRatePercent, r);
          })
        );
        return {
          title: 'GST Rate Summary',
          columns: ['GST %', 'Taxable', 'CGST', 'SGST', 'Total'],
          rows: Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([rate, v]) => [`${rate}%`, m(v.taxable), m(v.tax / 2), m(v.tax / 2), m(v.taxable + v.tax)]),
          numericCols: [1, 2, 3, 4],
        };
      }

      case 'b2b': {
        const rows = completed
          .filter((s) => gstinOf(s.customerId))
          .map((s) => {
            const tax = s.items.reduce((a, i) => a + i.total * (i.taxRatePercent / 100), 0);
            const taxable = s.items.reduce((a, i) => a + i.total, 0);
            return [formatDate(s.timestamp), s.billNo, s.customerName ?? '—', gstinOf(s.customerId) ?? '', m(taxable), m(tax), m(s.total)];
          });
        return {
          title: 'B2B Sales',
          columns: ['Date', 'Bill No', 'Customer', 'GSTIN', 'Taxable', 'Tax', 'Total'],
          rows,
          numericCols: [4, 5, 6],
        };
      }

      case 'b2c': {
        // Aggregated by rate for unregistered customers (GSTR-3B B2C style).
        const map = new Map<number, { taxable: number; tax: number }>();
        completed
          .filter((s) => !gstinOf(s.customerId))
          .forEach((s) =>
            s.items.forEach((i) => {
              const r = map.get(i.taxRatePercent) ?? { taxable: 0, tax: 0 };
              r.taxable += i.total;
              r.tax += i.total * (i.taxRatePercent / 100);
              map.set(i.taxRatePercent, r);
            })
          );
        return {
          title: 'B2C Sales',
          columns: ['GST %', 'Taxable', 'CGST', 'SGST', 'Total'],
          rows: Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([rate, v]) => [`${rate}%`, m(v.taxable), m(v.tax / 2), m(v.tax / 2), m(v.taxable + v.tax)]),
          numericCols: [1, 2, 3, 4],
        };
      }

      default:
        return null;
    }
  }, [active, completed, customers, products, formatMoney]); // eslint-disable-line

  const exportCsv = () => {
    if (!result) return;
    const csv = [result.columns, ...result.rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printUsb = () => {
    if (!result) return;
    printReportAuto(result.title, `GST · ${start} -> ${end}`, result.columns, result.rows);
  };

  return (
    <div className="section-page rp-page">
      <div className="page-header">
        <h1>GST REPORT</h1>
        <div className="rp-range">
          <label>Start <input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label>End <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
      </div>

      <div className="rp-section">
        <div className="rp-cards">
          {CARDS.map((c) => (
            <button key={c.key} className={`rp-card ${active === c.key ? 'active' : ''}`} onClick={() => setActive(c.key)}>
              <span className="rp-card-title">{c.title}</span>
              <span className="rp-card-desc">{c.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {result && (
        <div className="section-card" style={{ marginTop: 12 }}>
          <div className="rp-result-head">
            <h2>{result.title}</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-grey" onClick={() => window.print()}>Print</button>
              <button className="btn-grey" onClick={printUsb}>🖨 USB Print</button>
              <button className="btn-grey" onClick={exportCsv}>CSV</button>
              <button className="btn-teal" onClick={() => setActive(null)}>Close</button>
            </div>
          </div>
          <p className="muted" style={{ marginBottom: 8 }}>{start} → {end}</p>
          <div className="rp-result-scroll">
            <table className="section-table">
              <thead>
                <tr>
                  {result.columns.map((c, i) => (
                    <th key={i} className={result.numericCols?.includes(i) || i === result.columns.length - 1 ? 'right' : ''}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 ? (
                  <tr><td colSpan={result.columns.length} className="muted">No data in range.</td></tr>
                ) : (
                  result.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} className={result.numericCols?.includes(ci) || ci === row.length - 1 ? 'right' : ''}>{cell}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstReports;
